/**
 * Override de apiClient para integración SaaS.
 *
 * Único cambio respecto al original: API_BASE_URL usa ruta relativa en
 * lugar de http://localhost:3434/api — nginx del contenedor enruta /api/*
 * al backend SaaS (port 8080).
 *
 * Mantiene TODAS las exportaciones nombradas del original para no romper
 * ningún import existente en core/frontend.
 */
import axios from 'axios';

// Ruta relativa: nginx del contenedor enruta todo al API SaaS
export const API_BASE_URL = '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// ========================================
// USER MANAGEMENT FUNCTIONS
// ========================================

export const getUsers = async (params = {}) => {
  const response = await apiClient.get('/users', { params });
  return response.data;
};

export const createUser = async (userData) => {
  const response = await apiClient.post('/users', userData);
  return response.data;
};

export const inviteMember = async (orgId, payload) => {
  const response = await apiClient.post(`/v1/orgs/${orgId}/members/invite`, payload);
  return response.data;
};

export const getOrgMembers = async (orgId) => {
  const response = await apiClient.get(`/v1/orgs/${orgId}/members`);
  return response.data;
};

export const changeOrgMemberRole = async (orgId, userId, role) => {
  const response = await apiClient.patch(`/v1/orgs/${orgId}/members/${userId}/role`, { role });
  return response.data;
};

export const revokeOrgMember = async (orgId, userId) => {
  const response = await apiClient.delete(`/v1/orgs/${orgId}/members/${userId}`);
  return response.data;
};

export const previewInvitation = async (token) => {
  const response = await apiClient.get(`/v1/orgs/invitations/preview`, { params: { token } });
  return response.data;
};

export const acceptInvitation = async (orgId, token) => {
  const response = await apiClient.post(`/v1/orgs/${orgId}/members/accept`, { token });
  return response.data;
};

export const updateUserRole = async (userId, role) => {
  const response = await apiClient.put(`/users/${userId}/role`, { role });
  return response.data;
};

export const updateUserActive = async (userId, isActive) => {
  const response = await apiClient.put(`/users/${userId}/active`, { is_active: isActive });
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await apiClient.delete(`/users/${userId}`);
  return response.data;
};

export const getAuditLog = async (params = {}) => {
  const response = await apiClient.get('/admin/audit-log', { params });
  return response.data;
};

// ========================================
// DASHBOARD FUNCTIONS
// ========================================

export const getDashboardStats = async (projectId = null) => {
  const params = {};
  if (projectId) params.project_id = projectId;
  const response = await apiClient.get('/dashboard/stats', { params });
  return response.data;
};
